<template>
  <div id="contour-layer"></div>
</template>

<script>
import { throttle } from "lodash";
import { createNamespacedHelpers } from "vuex";

// Store
const {
  mapActions: mapActionsOfLiveMap,
  mapState: mapStateOfLiveMap,
  mapGetters: mapGettersOfLiveMap,
} = createNamespacedHelpers("liveMap");

// 気圧の表示方法を変更する基準となるズームレベル
// 限界までズームをはなした状態からマップ左下のズームボタン1回押下後、タッチパッドで少し近づけた状態
const LEVEL_TO_DISPLAY_PRESSURE_VALUE = 2.5;

export default {
  name: "ContourLayer",
  data() {
    return {
      zoom: 0,
    };
  },
  props: {
    map: Object,
    isReady: { type: Boolean, default: false },
    mapEvent: String,
  },
  computed: {
    ...mapGettersOfLiveMap([]),
    ...mapStateOfLiveMap({
      updateContourSourceLayer: state => state.updateContourSourceLayer,
      showContour: state => state.showContour,
    }),
    contourTextField() {
      const textFiled =
        this.zoom >= LEVEL_TO_DISPLAY_PRESSURE_VALUE
          ? [
              "case",
              ["==", ["get", "contourType"], "high"],
              ["concat", "H\n", ["get", "pressure"]],
              ["==", ["get", "contourType"], "low"],
              ["concat", "L\n", ["get", "pressure"]],
              ["get", "pressure"],
            ]
          : [
              "case",
              ["==", ["get", "contourType"], "high"],
              "H\n",
              ["==", ["get", "contourType"], "low"],
              "L\n",
              "",
            ];
      return textFiled;
    },
  },
  watch: {
    mapEvent(val) {
      if (!this.isReady) return;
      if (!this.showContour) return;
      if (val !== "mapZoomEnd") return;
      this.zoom = this.map.getZoom();
    },
    async isReady() {
      if (this.isReady) {
        if (this.showContour) {
          this.zoom = this.map.getZoom();
          await this.setContourSourceLayer(this.updateContourSourceLayer);
        }
      }
    },
    updateContourSourceLayer(val) {
      if (val.isUpdate) {
        const shouldBeUpdated = val.newSourceLayerId !== val.oldSourceLayerId;
        if (!shouldBeUpdated) return;
        this.updateSourceLayer(this, val);
      } else {
        this.removeContourSourceLayer(val.newSourceLayerId);
      }
    },
    contourTextField: {
      handler() {
        if (!this.isReady) return;
        if (!this.showContour) return;
        if (!this.map.getSource("contour")) return;
        if (!this.map.getLayer("contour_text")) return;
        this.map.setLayoutProperty(
          "contour_text",
          "text-field",
          this.contourTextField
        );
      },
      deep: true,
    },
  },
  methods: {
    ...mapActionsOfLiveMap([]),
    updateSourceLayer: throttle((self, val) => {
      self.setContourSourceLayer(val);
    }, 100),
    async removeContourSourceLayer() {
      if (this.map.getLayer("contour_line")) this.map.removeLayer("contour_line");
      if (this.map.getLayer("contour_text")) this.map.removeLayer("contour_text");
      if (this.map.getSource("contour")) this.map.removeSource("contour");
    },

    async setContourSourceLayer(val) {
      if (typeof val === "undefined") return;
      await this.removeContourSourceLayer(val.oldSourceLayerId);

      this.map.addSource("contour", {
        type: "vector",
        url: `mapbox://aidea.${val.newSourceLayerId}?fresh=true`,
      });

      const errorHandler = (e) => {
        if (e.sourceId === "contour") {
          console.error('Contour source load error:', e.error);
          this.$vs.notify({
            title: "現在一部の気象データを取得できません。時間をおいて再度お試しください。",
            color: "danger",
            iconPack: "feather",
            icon: "icon-cloud-off",
          });
          this.map.off('error', errorHandler);
        }
      };
      this.map.on('error', errorHandler);

      // 等圧線
      this.map.addLayer({
        id: "contour_line",
        type: "line",
        source: "contour",
        "source-layer": "contour_line",
        paint: {
          "line-color": "#13CB73",
          "line-width": [
            "case",
            ["==", ["%", ["to-number", ["get", "pressure"]], 20], 0], 4,
            1, // fallback
          ],
        },
      });

      // 気圧の種類・気圧値
      this.map.addLayer({
        id: "contour_text",
        type: "symbol",
        source: "contour",
        "source-layer": "contour_point",
        layout: {
          visibility: this.showContour ? "visible" : "none",
          "text-field": this.contourTextField,
          "text-size": 14,
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-halo-color": "rgba(255, 255, 255, 0.8)",
          "text-halo-width": 1.6,
          "text-color": [
            "case",
            ["==", ["get", "contourType"], "high"], "#0076E9",
            ["==", ["get", "contourType"], "low"], "#E40052",
            "#383838", // fallback
          ],
        },
      });
    },
  },
};
</script>

<style lang="scss"></style>
